import React, { useState, useEffect } from "react";
import { API_BASE } from "@/lib/api";

/**
 * Cover art image with initials fallback.
 *
 * Props:
 *  - song: song object (uses id, has_cover, title, artist, cover_updated_at)
 *  - className: classes for the wrapper (the wrapper sets the size)
 *  - rounded: tailwind rounding class (default "rounded-sm")
 *  - localPreviewUrl: optional URL to display instead of the remote cover (used while editing)
 *
 * The component shows initials when no cover exists or while the image is loading.
 */
export default function CoverArt({
  song,
  className = "",
  rounded = "rounded-sm",
  localPreviewUrl = null,
  initialsClassName = "text-[5rem] sm:text-[6rem]",
}) {
  const initial = (song?.title || song?.artist || "?").trim().slice(0, 1).toUpperCase();
  const hasRemote = !!song?.has_cover && !!song?.id;
  const remoteUrl = hasRemote
    ? `${API_BASE}/songs/${song.id}/cover${
        song.cover_updated_at ? `?v=${encodeURIComponent(song.cover_updated_at)}` : ""
      }`
    : null;
  const url = localPreviewUrl || remoteUrl;

  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setErrored(false);
  }, [url]);

  const showImage = !!url && !errored;

  return (
    <div
      className={`bg-[#1A1A1A] border border-white/5 group-hover:border-white/20 transition-colors relative overflow-hidden flex items-center justify-center ${rounded} ${className}`}
    >
      <span
        className={`font-display font-black leading-none text-white/10 select-none ${initialsClassName} ${
          showImage && loaded ? "opacity-0" : "opacity-100"
        } transition-opacity duration-300`}
      >
        {initial}
      </span>
      {showImage && (
        <img
          src={url}
          alt={song?.title || "cover"}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          data-testid="cover-art-img"
          crossOrigin="anonymous"
        />
      )}
    </div>
  );
}
